import { useEffect, useState } from 'react';
import './HomePage.css';

// The hero's worked example: one real lookup, end to end, on a module new in
// google-adk 2.x. Every line is evidence, not illustration (2026-11-04):
// - `memory` is what qwen/qwen3.5-9b wrote, without tools, when asked for this
//   graph agent; importing it from google-adk 2.13.0 raises ImportError.
// - `answer` is the first sentence of the actual get_context reply for
//   pypi/google-adk@2.13.0 to `question`, not a paraphrase.
// - `fixed` constructs against google-adk 2.13.0 (checked with uv).
const HERO_EXAMPLE = {
  ask: 'Build a graph agent with google-adk 2.13.0: one agent picks a city, another writes a short report on it.',
  memory: 'from google.adk import GraphAgent',
  memoryNote:
    'Invented. google-adk 2.13.0 has no GraphAgent, so the import fails before anything runs.',
  lookup: 'get_context · pypi/google-adk@2.13.0',
  question: 'How do I create a graph agent in this version?',
  // The reply's markdown `Workflow` is rendered as inline code.
  answer: ['In ADK 2.0, a graph agent is created using the ', 'Workflow', ' class.'],
  fixed:
    'from google.adk import Agent, Workflow\n\nroot_agent = Workflow(\n    name="root_agent",\n    edges=[("START", picker, writer)],\n)',
} as const;

// The homepage's evidence row: the headline numbers from the eval/copilot-
// codegen.ts matrix runs of 2026-11-04 (generic and named instruction). The
// argument page's "What we measured" section carries the method, per-model
// table, and caveats; keep both in step with the same run.
const EVIDENCE = [
  {
    label: 'Small open models that scored higher with llmcontext',
    value: '5 of 5',
    detail: 'When told which package to look up; 4 of 5 without that hint',
  },
  {
    label: 'Runs where the agent looked the API up',
    value: '31 of 35',
    detail: 'When told which package to look up; 22 of 35 without that hint',
  },
  {
    label: 'Model sizes tested',
    value: '8B–30B',
    detail: 'Qwen, Ministral, gpt-oss, and Gemma open models',
  },
  {
    label: 'Libraries, pinned to recent versions',
    value: '7',
    detail: 'Python, TypeScript, Rust, and Dart',
  },
] as const;

// What the project is for, ahead of how to adopt it. Each claim is one a reader
// can check: llmcontext's own pipeline gives indexing, selection, and answering
// separate model roles (OPENROUTER_INDEX/SELECT/ANSWER_MODEL); model endpoints
// are environment-driven (OPENROUTER_BASE_URL, VOYAGE_API_URL); the small-model
// result is from eval/copilot-codegen.ts; open source is stated as the plan it
// is, not as done.
const PROJECT_PILLARS = [
  [
    'Read the paper, then act.',
    'Nobody runs their day on what they remember of last year’s news. They read the paper, see what changed, and act on that — and when they need a detail, they look it up. A model’s memory stopped the day its training ended. llmcontext is its paper and its reference library: new releases are indexed as they ship, and any detail is one lookup away.',
  ],
  [
    'Narrow questions, checkable answers.',
    'Most of what a coding agent needs is not a vast context. It is a narrow question — what does this function take, in this version? — with an answer you can check. Ask it that way and small models do it well. llmcontext runs on the same idea: summarizing a file, choosing the passages, and writing the answer are separate steps, each a model role you can fill with a small one. In our tests, most of the small open models we tried (8B–30B) wrote more correct code for recently changed libraries when they looked the API up first.',
  ],
  [
    'Local models for private code.',
    'Private repositories are the code no model was trained on, and the code that should least leave your network. llmcontext self-hosts with Docker and Postgres, and every model it uses is replaceable: summaries and answers go to any OpenAI-compatible server — Ollama, vLLM, LiteLLM — and embeddings to an open-source embedding model you serve yourself. Index a private repository with models on your own hardware, and the code stays there.',
  ],
  [
    'Open, as the plan.',
    'We are working toward releasing llmcontext as open source, so the people who rely on it can read how it decides what counts as evidence, run it themselves, and change it. Querying public packages is already free and needs no API key.',
  ],
] as const;

const USE_PATHS = [
  {
    label: 'Public packages',
    title: 'Connect your agent and ask.',
    body: 'Python packages from PyPI, TypeScript packages from npm, Rust crates, Maven packages, and GitHub repositories are ready to use. Add llmcontext as an MCP server, or give your agent the link to an indexed library, and it can retrieve the version and information it needs.',
    links: [
      ['Set up MCP', '/docs'],
      ['Browse public libraries', '/search'],
    ],
  },
  {
    label: 'Private repositories',
    title: 'Index once, share with your team.',
    body: 'Create an account, connect your private repository, and index the versions your team uses. Your coding agents can then query the same reusable context while access stays controlled by your organization and workspace.',
    links: [['Create an account and index a repo', '/request']],
  },
  {
    label: 'Self-hosted',
    title: 'Run it where your code lives.',
    body: 'Deploy llmcontext with your own storage, Postgres, models, and access rules. We will help your team plan and set up the deployment, so call, text, or email us when you are ready to bring it into your infrastructure.',
    links: [['Talk to us about setup', '/contact']],
  },
] as const;

const INSTALL_COMMAND = 'curl -fsSL https://llmcontext.dev/install.sh | sh';

export function HomePage() {
  const [copied, setCopied] = useState(false);

  const copyInstall = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_COMMAND);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'llmcontext — give your coding agents up-to-date information';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing" data-testid="home-landing">
      <header className="landing-header">
        <nav className="landing-nav" aria-label="Primary navigation">
          <a className="landing-brand" href="#top">
            llmcontext.dev
          </a>
          <div className="landing-nav-links">
            <a href="#evidence">Evidence</a>
            <a href="#project">The project</a>
            <a href="#use">How to use it</a>
            <a href="/argument">The argument</a>
            <a className="landing-nav-cta" href="/docs">
              Get started
            </a>
          </div>
        </nav>
      </header>

      <section className="landing-hero" id="top" aria-labelledby="landing-hero-title">
        <div className="landing-hero-inner">
          <p className="landing-eyebrow">An evidence concierge for coding agents</p>
          <h1 id="landing-hero-title">
            <span>Give your coding agents</span>
            <span>up-to-date information.</span>
          </h1>
          <p className="landing-hero-deck">
            Your coding agent asks; llmcontext looks it up. It works like a concierge: it finds the
            answer in the exact versions your project uses &mdash; public packages and private
            repositories alike &mdash; and hands back only what the agent needs. No relearning every
            library, no scraping docs in every session &mdash; and it is built to let smaller, local
            models do work that usually takes a frontier model.
          </p>
          <div className="landing-actions">
            <a className="landing-button landing-button-primary" href="/docs">
              Get started
            </a>
            <a className="landing-button landing-button-secondary" href="/argument">
              Read the argument
            </a>
          </div>
          <figure
            className="landing-hero-example"
            aria-label="Example: an agent looks up google-adk before building a graph agent"
          >
            <div className="landing-hero-step">
              <p className="landing-hero-step-label">You ask your agent</p>
              <div className="landing-hero-step-body">
                <p className="landing-hero-step-question">&ldquo;{HERO_EXAMPLE.ask}&rdquo;</p>
              </div>
            </div>
            <div className="landing-hero-step">
              <p className="landing-hero-step-label">From memory, it writes</p>
              <div className="landing-hero-step-body">
                <code>{HERO_EXAMPLE.memory}</code>
                <p className="landing-hero-step-note">
                  <span className="landing-status-dot warn" />
                  {HERO_EXAMPLE.memoryNote}
                </p>
              </div>
            </div>
            <div className="landing-hero-step">
              <p className="landing-hero-step-label">It asks llmcontext</p>
              <div className="landing-hero-step-body">
                <code>{HERO_EXAMPLE.lookup}</code>
                <p className="landing-hero-step-question">&ldquo;{HERO_EXAMPLE.question}&rdquo;</p>
              </div>
            </div>
            <div className="landing-hero-step">
              <p className="landing-hero-step-label">llmcontext answers</p>
              <div className="landing-hero-step-body">
                <blockquote>
                  {HERO_EXAMPLE.answer[0]}
                  <code>{HERO_EXAMPLE.answer[1]}</code>
                  {HERO_EXAMPLE.answer[2]}
                </blockquote>
              </div>
            </div>
            <div className="landing-hero-step">
              <p className="landing-hero-step-label">With llmcontext, it writes</p>
              <div className="landing-hero-step-body">
                <code>{HERO_EXAMPLE.fixed}</code>
                <p className="landing-hero-step-note">
                  <span className="landing-status-dot good" />
                  Checked against the version you run, before the code exists.
                </p>
              </div>
            </div>
          </figure>
          <p className="landing-lookup-preview">
            <a href="/search">Browse the public libraries already indexed &rarr;</a>
          </p>
        </div>
      </section>

      <section
        className="landing-principles"
        id="evidence"
        aria-labelledby="landing-evidence-title"
      >
        <div className="landing-principles-inner">
          <div className="landing-section-heading">
            <h2 id="landing-evidence-title">What we measured.</h2>
            <p>
              We gave five small open models the same seven tasks twice &mdash; once working from
              memory, once able to look things up in llmcontext &mdash; on libraries recent enough
              for models to get wrong.
            </p>
          </div>
          <dl className="landing-evidence-grid">
            {EVIDENCE.map(({ label, value, detail }) => (
              <div className="landing-evidence-tile" key={label}>
                <dt>{label}</dt>
                <dd className="landing-evidence-value">{value}</dd>
                <dd className="landing-evidence-detail">{detail}</dd>
              </div>
            ))}
          </dl>
          <p className="landing-evidence-note">
            One run per task, graded against checklists verified for each pinned version: read these
            as direction, not effect size.{' '}
            <a href="/argument#evidence">
              Method, per-model results, and what it does not do yet &rarr;
            </a>
          </p>
        </div>
      </section>

      <section className="landing-principles" id="project" aria-labelledby="landing-project-title">
        <div className="landing-principles-inner">
          <div className="landing-section-heading">
            <h2 id="landing-project-title">Small questions, small models.</h2>
            <p>
              Large models earn their size holding a large context. A coding agent mostly needs
              something smaller: the right fact, from the right version, at the moment it acts.
              Split the work into questions that size and give each one a place to look, and small
              models working together go a long way &mdash; on your own hardware, if you like.
            </p>
          </div>
          <div className="landing-principle-grid">
            {PROJECT_PILLARS.map(([heading, body], index) => (
              <article className="landing-principle" key={heading}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{heading}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-use" id="use" aria-labelledby="landing-use-title">
        <div className="landing-use-inner">
          <div className="landing-section-heading">
            <h2 id="landing-use-title">Use llmcontext your way.</h2>
            <p>
              Start with public libraries in minutes, bring your team&rsquo;s private repositories
              when you need them, or run the entire context layer inside your own infrastructure.
              Each path gives coding agents the same simple habit: look it up before acting.
            </p>
          </div>
          <div className="landing-use-grid">
            {USE_PATHS.map(({ label, title, body, links }, index) => (
              <article className="landing-use-item" key={label}>
                <div className="landing-use-meta">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <span>{label}</span>
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
                <div className="landing-use-links">
                  {links.map(([linkLabel, linkHref]) => (
                    <a href={linkHref} key={linkLabel}>
                      {linkLabel} →
                    </a>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-finale" id="install" aria-labelledby="landing-install-title">
        <div className="landing-finale-inner">
          <p className="landing-kicker">Start with evidence</p>
          <h2 id="landing-install-title">Install in one command.</h2>
          <p>
            One command adds the MCP server and the agent skill to Claude Code, Cursor, Windsurf,
            and VS Code. Public packages need no API key — ask your agent a version-pinned question
            and watch it answer from evidence.
          </p>
          <div className="landing-install">
            <code>{INSTALL_COMMAND}</code>
            <button type="button" onClick={copyInstall} aria-label="Copy install command">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="landing-finale-actions">
            <a className="landing-button landing-button-light" href="/contact">
              Contact us
            </a>
          </div>
        </div>
      </section>
      <footer className="landing-footer">
        llmcontext.dev · Up-to-date information for coding agents
      </footer>
    </div>
  );
}
